import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { AppSidebar, AppFooter, AppHeader } from '../components/index';
import { AuthUserContext, withAuthorization } from '../views/session';
import { withFirebase } from '../views/firebase';
import Dashboard from '../views/dashboard/Dashboard';
import { ActiveLinkProvider } from '../components/ActiveLinkContext';
import { OnboardingProvider, useOnboarding } from '../context/OnboardingContext';
import OnboardingWizard from '../views/pages/onboarding/OnboardingWizard';
import { CSpinner } from '@coreui/react';

import '../scss/main.scss';
const DefaultLayout = ({ firebase }) => {
  const authContext = useContext(AuthUserContext);
  const authUser = authContext?.authUser || authContext;
  const navigate = useNavigate();
  const location = useLocation(); // Get the current location
  const [isCheckingToken, setIsCheckingToken] = useState(false); // Prevents multiple checks at the same time
  // Fetch the FHIR access token from localStorage
  const [mieFhirAccessToken, setMieFhirAccessToken] = useState(localStorage.getItem('mieFToken'));


  useEffect(() => {
    if (isCheckingToken) return; // Prevent multiple checks
    const appleAccessToken = localStorage.getItem('appleAccessToken');
    const googleAccessToken = localStorage.getItem('googleAccessToken');
    const mieFhirAccessToken = localStorage.getItem('mieFToken');
    
    const tokenExpiryTime = localStorage.getItem('tokenExpiryTime');
    if(mieFhirAccessToken){
      //console.log("fhir access token present");
    }else if (appleAccessToken) {
      //console.log("apple access token present");
    } else if (!googleAccessToken || !tokenExpiryTime || isNaN(tokenExpiryTime)) {
        const currentTime = Date.now();
        const timeLeft = tokenExpiryTime - currentTime;

        if (timeLeft <= 0) {
          // Token has expired
          if (location.pathname !== "/login") {
            setIsCheckingToken(true); // Prevent further checks
            navigate("/login"); // Redirect to login
            setIsCheckingToken(false); // Prevent further checks
          }
        } else if (timeLeft <= 5 * 60 * 1000) {
          // Token is about to expire
          const userWantsToRenew = window.confirm("Your session is about to expire. Would you like to renew your session?");
          if (!userWantsToRenew && location.pathname !== "/login") {
            setIsCheckingToken(true);
            navigate("/login"); // Redirect if the user doesn't want to renew
            setIsCheckingToken(false); // Prevent further checks
          }
        }
      } else if (location.pathname !== "/login") {
        setIsCheckingToken(true);
        navigate("/login"); // Redirect if no token is found
        setIsCheckingToken(false); // Prevent further checks
      }
  }, [navigate, location, isCheckingToken]);


  // Sync mieFhirAccessToken when localStorage changes
  useEffect(() => {
    const syncToken = () => {
      const token = localStorage.getItem('mieFToken');
      if (token !== mieFhirAccessToken) {
        setMieFhirAccessToken(token);
      }
    };

    window.addEventListener('storage', syncToken); // Listen for storage changes
    return () => window.removeEventListener('storage', syncToken); // Cleanup
  }, [mieFhirAccessToken]);


  const signOut = e => {
    e.preventDefault();
    firebase.doSignOut();
  };

  //console.log("DefaultLayout: authUser", authUser);

  if (!authUser) {
    return (
      <div className="mieros-gradient-bg min-vh-100 d-flex align-items-center justify-content-center">
        <CSpinner color="info" />
      </div>
    );
  }

  return (
    <ActiveLinkProvider>
      <OnboardingProvider uid={authUser?.uid}>
        <InnerLayout
          authUser={authUser}
          mieFhirAccessToken={mieFhirAccessToken}
          location={location}
        />
      </OnboardingProvider>
    </ActiveLinkProvider>
  );
};

/**
 * InnerLayout — separated so it can consume OnboardingContext.
 * Shows the OnboardingWizard as a full-page overlay when onboarding is not complete.
 */
const InnerLayout = ({ authUser, mieFhirAccessToken, location }) => {
  const { onboardingComplete, loading } = useOnboarding();

  // While the context is loading flags, show a centered spinner
  if (loading) {
    return (
      <div
        className="mieros-gradient-bg min-vh-100 d-flex align-items-center justify-content-center"
      >
        <CSpinner color="info" />
      </div>
    );
  }

  // If the user has not completed onboarding, show the wizard (blocking overlay)
  if (!onboardingComplete) {
    return <OnboardingWizard />;
  }

  // Normal authenticated layout
  return (
    <div>
      <AppSidebar authUser={authUser} fhirToken={mieFhirAccessToken} />
      <div className="wrapper d-flex flex-column min-vh-100 mieros-gradient-bg">
        <AppHeader authUser={authUser} fhirToken={mieFhirAccessToken} />
        <div className="body flex-grow-1 px-3">
          {/* Conditionally render Dashboard based on a route or logic */}
          {location.pathname === '/dashboard' && (
            <Dashboard authUser={authUser} fhirToken={mieFhirAccessToken} />
          )}
          {/* Use Outlet for other routes */}
          <Outlet authUser={authUser} fhirToken={mieFhirAccessToken} />
        </div>
        <AppFooter authUser={authUser} fhirToken={mieFhirAccessToken} />
      </div>
    </div>
  );
};

const condition = authUser => !!authUser;
const DefaultLayoutWithFirebase = withFirebase(DefaultLayout);
const DefaultLayoutWithAuthorization = withAuthorization(condition)(DefaultLayoutWithFirebase);

export default DefaultLayoutWithAuthorization;
